<template>
  <app-right-sidebar
    :width="640"
    :show-sidebar="getShowSidebar"
    :form-invalid="$v.$invalid"
    :disable-button="disableButton"
    :sidebar-title="getSidebarTitle"
    title-style="center"
    buttons-style="center"
    @hideSidebar="hideSidebar($event)"
    @saveData="saveData"
  >
    <form :class="$style.addVehicleForm">
      <div :class="$style.formMain">
        <div :class="$forms.formRow">

          <div :class="[$forms.inputGroup, $forms.fullWidthGroup]">
            <app-select-group
              label="What Type of Vehicle do You Own?*"
              :value="getVehicleType"
              :items="getVehicleTypes"
              v-model="$v.vehicle.vehicleType.$model"
              @input="setVehicleType"
            />

            <p v-if="$v.vehicle.vehicleType.$error" :class="$forms.errorMessage">
              Vehicle Type is required
            </p>
          </div>
        </div>

        <div :class="$forms.formRow">
          <div :class="$style.balanceOptions">
            <h6>Is there a balance on this vehicle?*
              <app-tooltip>
                <template v-slot:tooltipHeader>
                  Vehicle Balance
                </template>
                <template v-slot:tooltipText>
                  If you have any payments left on your vehicle within the next 30 days,
                  select "Yes".
                  <br /><br />
                  If you own this vehicle free and clear and do not have any (further) payments
                  due on this vehicle, select "No".
                </template>
              </app-tooltip>
            </h6>
            <app-radio-buttons :options="balanceOptions"
                               :value="getBalance"
                               v-model="$v.vehicle.isThereABalance.$model"
                               @input="changeBalance" />
          </div>
        </div>

        <div :class="$forms.formRow">
          <div :class="[$forms.inputGroup, $style.inputGroup]">
            <app-currency-input v-model="$v.vehicle.currentValue.$model"
                                :value="vehicle.currentValue"
                                label="Current Value*"/>
            <p v-if="$v.vehicle.currentValue.$error"
               :class="$forms.errorMessage">
              Current Value is invalid
            </p>
          </div>

          <div :class="[$forms.inputGroup, $style.inputGroup]">
            <app-select-group
              label="Year Purchased*"
              :value="getYearPurchased"
              :items="getYears"
              v-model="$v.vehicle.yearPurchased.$model"
              @input="setYearPurchased"
            />
            <p v-if="$v.vehicle.yearPurchased.$error"
               :class="$forms.errorMessage">
              Year Purchased is required
            </p>
          </div>
        </div>

      </div>
      <div v-if="vehicle.isThereABalance">
        <div :class="$forms.formRow">
          <div :class="[$forms.inputGroup, $style.inputGroup]">
            <app-currency-input v-model="$v.vehicle.monthlyPayment.$model"
                                :value="vehicle.monthlyPayment"
                                label="Monthly Payment(s)*"/>
            <p v-if="$v.vehicle.monthlyPayment.$error"
               :class="$forms.errorMessage">
              Monthly Payment must be greater that 0
            </p>
          </div>

          <div :class="[$forms.inputGroup, $style.inputGroup]">
            <app-currency-input v-model="$v.vehicle.balanceOwed.$model"
                                :value="vehicle.balanceOwed"
                                label="Balance Owed*"/>
            <p v-if="$v.vehicle.balanceOwed.$error"
               :class="$forms.errorMessage">
              Balance Owed must be greater that 0
            </p>
          </div>
        </div>

        <div :class="$style.finalPayment">
          <h6>Final Payment</h6>

          <div :class="$forms.formRow">

            <div :class="[$forms.inputGroup, $style.finalPaymentFields]">
              <app-select-group
                label="Month*"
                :value="getFinalPaymentMonth"
                :class="$style.month"
                :items="getFinalPaymentMonths"
                v-model="$v.vehicle.finalPaymentMonth.$model"
                @input="setFinalPaymentMonth"
              />

              <app-select-group
                label="Year*"
                :value="getFinalPaymentYear"
                :class="$style.year"
                :items="getFinalPaymentYears"
                v-model="$v.vehicle.finalPaymentYear.$model"
                @input="setFinalPaymentYear"
              />
            </div>
          </div>
        </div>

      </div>

      <div :class="$style.carDetails">
        <h6>Car Details</h6>

        <div :class="$forms.formRow">

          <div :class="[$forms.inputGroup, $style.year]">
            <app-select-group
              label="Model Year*"
              :value="getModelYear"
              :class="$style.year"
              :items="getYears"
              v-model="$v.vehicle.modelYear.$model"
              @input="setModelYear"
            />
            <p v-if="$v.vehicle.modelYear.$error"
               :class="$forms.errorMessage">
              Model Year is required
            </p>
          </div>

          <div :class="[$forms.inputGroup, $style.shortGroup]">
            <app-input-group v-model.trim="$v.vehicle.make.$model"
                             label="Make*"
                             type="text"/>

            <p v-if="isMakeRequired"
               :class="$forms.errorMessage">
              Make is required
            </p>
            <p v-if="isMakeTooLong"
               :class="$forms.errorMessage">
              Make can't be more than 100 characters
            </p>
          </div>

          <div :class="[$forms.inputGroup, $style.shortGroup]">
            <app-input-group v-model.trim="$v.vehicle.model.$model"
                             label="Model*"
                             type="text"/>

            <p v-if="isModelRequired"
               :class="$forms.errorMessage">
              Model is required
            </p>
            <p v-if="isModelTooLong"
               :class="$forms.errorMessage">
              Model can't be more than 100 characters
            </p>
          </div>
        </div>
      </div>

      <div :class="$forms.formRow">
        <div :class="[$forms.inputGroup, $style.inputGroup]">
          <h6>Mileage*</h6>
          <app-input-group v-model="$v.vehicle.mileage.$model"
                           :value="vehicle.mileage"
                           type="number"
                           label=""/>
          <p v-if="$v.vehicle.mileage.$error"
             :class="$forms.errorMessage">
            Mileage is invalid
          </p>
        </div>
      </div>

    </form>

    <div v-if="serverError">
      <p :class="$forms.errorMessage">
        {{ serverError }}
      </p>
    </div>
  </app-right-sidebar>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { maxLength, required, requiredIf } from 'vuelidate/lib/validators';
import { getPossibleYears, getFutureMonths, getFutureYears } from '@/utils';
import { mustBePositive, mustBePositiveIfThereIsBalance } from '@/utils/validation';
import { transformMonthToNumber, transformMonthToString } from '@/utils/transforms';
import { MONTHS, VEHICLE_TYPES } from '@/constants';

export default {
  props: {
    showSidebar: {
      type: Boolean,
      default: false,
    },
    sidebarType: {
      type: String,
      default: 'add',
    },
    editedItem: {
      type: Object,
      default: null,
    },
    editedItemId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      serverError: '',
      vehicle: {
        vehicleType: '',
        isThereABalance: false,
        currentValue: 0,
        yearPurchased: '',
        monthlyPayment: 0,
        balanceOwed: 0,
        finalPaymentMonth: '',
        finalPaymentYear: '',
        modelYear: '',
        make: '',
        model: '',
        mileage: '0',
      },
      balanceOptions: [
        {
          label: 'Yes',
          value: true,
        },
        {
          label: 'No',
          value: false,
        },
      ],
      disableButton: false,
      initialVehicle: null,
    };
  },
  methods: {
    ...mapActions([
      'postNewVehicle',
      'editVehicle',
    ]),
    hideSidebar(e) {
      this.clearFields();
      this.$emit('hideSidebar', e);
    },
    saveData() {
      if (this.lodash.isEqual(this.initialVehicle, this.vehicle)) {
        this.hideSidebar();
        return;
      }

      /* Disable Save button in sidebar to prevent double clicking */
      this.disableButton = true;

      const newItem = {
        ...this.vehicle,
        yearPurchased: +this.vehicle.yearPurchased,
        finalPaymentYear: +this.vehicle.finalPaymentYear,
        modelYear: +this.vehicle.modelYear,
        vehicleType: VEHICLE_TYPES.find(type => type.textValue === this.vehicle.vehicleType).code,
        finalPaymentMonth: transformMonthToNumber(this.vehicle.finalPaymentMonth),
      };

      if (this.sidebarType === 'edit') {
        newItem.id = this.editedItemId;

        this.editVehicle({
          editedItemId: newItem.id,
          newItem,
          userId: this.getUserId,
        })
          .then(() => {
            this.hideSidebar();
          })
          .catch((err) => {
            this.serverError = err.response.data.title;
            this.disableButton = false;
          });
      } else {
        this.postNewVehicle({
          newItem,
          userId: this.getUserId,
        })
          .then(() => {
            this.hideSidebar();
          })
          .catch((err) => {
            this.serverError = err.response.data.title;
            this.disableButton = false;
          });
      }
    },
    clearFields() {
      Object.keys(this.vehicle).forEach((key) => {
        this.vehicle[key] = '';
      });
      this.vehicle.isThereABalance = false;
      this.vehicle.currentValue = 0;
      this.vehicle.balanceOwed = 0;
      this.vehicle.monthlyPayment = 0;
      this.vehicle.mileage = '0';
      this.$v.$reset();
      this.disableButton = false;
    },
    setFormFields() {
      Object.keys(this.vehicle).forEach((key) => {
        if (this.editedItem[key]) {
          this.vehicle[key] = this.editedItem[key];
        }
      });
      this.vehicle.yearPurchased = this.vehicle.yearPurchased.toString();
      this.vehicle.modelYear = this.vehicle.modelYear.toString();
      this.vehicle.mileage = this.vehicle.mileage.toString();
      if (this.editedItem.finalPaymentMonth) {
        this.vehicle.finalPaymentMonth = transformMonthToString(this.editedItem.finalPaymentMonth);
        this.vehicle.finalPaymentYear = this.vehicle.finalPaymentYear.toString();
      }

      this.vehicle.vehicleType = VEHICLE_TYPES.find(type => type
        .code === this.editedItem.vehicleType).textValue;

      this.initialVehicle = { ...this.vehicle };
    },
    resetServerErrors() {
      this.serverError = '';
    },
    /* Change the value of the select */
    setVehicleType(val) {
      this.vehicle.vehicleType = val;
    },
    /* Change the value of the select */
    setFinalPaymentMonth(val) {
      this.vehicle.finalPaymentMonth = val;
    },
    /* Change the value of the select */
    setFinalPaymentYear(val) {
      this.vehicle.finalPaymentYear = val;
    },
    /* Change the value of the select */
    setModelYear(val) {
      this.vehicle.modelYear = val;
    },
    /* Change the value of the radio buttons input */
    changeBalance(option) {
      if (!option) {
        this.vehicle.finalPaymentMonth = '';
        this.vehicle.finalPaymentYear = '';
        this.vehicle.balanceOwed = 0;
        this.vehicle.monthlyPayment = 0;
      }
      this.vehicle.isThereABalance = option;
    },
    /* Change the value of the select */
    setYearPurchased(val) {
      this.vehicle.yearPurchased = val;
    },
  },
  computed: {
    ...mapGetters([
      'getUserId',
    ]),
    getMonths() {
      return MONTHS;
    },
    getVehicleTypes() {
      return VEHICLE_TYPES.map(type => type.textValue).sort();
    },
    getShowSidebar() {
      return this.showSidebar;
    },
    getSidebarTitle() {
      return this.sidebarType === 'edit' ? 'Edit Vehicle' : 'Add New Vehicle';
    },
    /* Get vehicleType for displaying in table */
    getVehicleType() {
      return this.vehicle.vehicleType;
    },
    /* Get finalPaymentMonth */
    getFinalPaymentMonth() {
      return this.vehicle.finalPaymentMonth;
    },
    /* Get finalPaymentYear */
    getFinalPaymentYear() {
      return this.vehicle.finalPaymentYear;
    },
    /* Get finalPaymentYear */
    getModelYear() {
      return this.vehicle.modelYear;
    },
    /* Get the current value of the radio buttons input */
    getBalance() {
      return this.vehicle.isThereABalance;
    },
    /* Get value from the select field */
    getYearPurchased() {
      return this.vehicle.yearPurchased;
    },
    /* Get possible years array for select */
    getYears() {
      return getPossibleYears();
    },
    getFinalPaymentYears() {
      return getFutureYears();
    },
    getFinalPaymentMonths() {
      const months = getFutureMonths(+this.getFinalPaymentYear);
      if (months.indexOf(this.getFinalPaymentMonth) < 0) {
        this.setFinalPaymentMonth('');
      }
      return months;
    },
    isMakeRequired() {
      return !this.$v.vehicle.make.required && this.$v.vehicle.make.$dirty;
    },
    isMakeTooLong() {
      return !this.$v.vehicle.make.maxLength && this.$v.vehicle.make.$dirty;
    },
    isModelRequired() {
      return !this.$v.vehicle.model.required && this.$v.vehicle.model.$dirty;
    },
    isModelTooLong() {
      return !this.$v.vehicle.model.maxLength && this.$v.vehicle.model.$dirty;
    },
  },
  watch: {
    getShowSidebar() {
      if (this.showSidebar) {
        this.resetServerErrors();

        if (this.sidebarType === 'edit') {
          this.setFormFields();
        }
      }
    },
  },
  validations: {
    vehicle: {
      vehicleType: {
        required,
      },
      isThereABalance: {
        required,
      },
      currentValue: {
        required,
        mustBePositive,
      },
      yearPurchased: {
        required,
      },
      monthlyPayment: {
        required: requiredIf(vm => vm.isThereABalance),
        mustBePositiveIfThereIsBalance,
      },
      balanceOwed: {
        required: requiredIf(vm => vm.isThereABalance),
        mustBePositiveIfThereIsBalance,
      },
      finalPaymentMonth: {
        required: requiredIf(vm => vm.isThereABalance),
      },
      finalPaymentYear: {
        required: requiredIf(vm => vm.isThereABalance),
      },
      modelYear: {
        required,
      },
      make: {
        required,
        maxLength: maxLength(100),
      },
      model: {
        required,
        maxLength: maxLength(100),
      },
      mileage: {
        required,
        mustBePositive,
      },
    },
  },
};
</script>

<style src="@/styles/forms.scss" lang="scss" module="$forms"></style>
<style lang="scss" module>
  @import "@/styles/variables.scss";

  .addVehicleForm {
    display: flex;
    flex-direction: column;
    margin-top: 20px;

    .balanceOptions {
      margin-top: 26px;
      margin-bottom: 12px;

      h6 {
        margin-bottom: 4px;
      }

      div {
        max-width: 190px;
      }
    }

    .inputGroup {
      width: 268px;
      margin-right: 24px;

      &:last-child {
        margin-right: 0;
      }

      & > div {
        max-width: 100%;

        input {
          max-width: 268px;
        }
      }
    }

    .finalPayment {
      margin-top: 36px;

      h6 {
        margin-bottom: 16px;
      }

      &Fields {
        display: flex;

        .month {
          width: 144px;
          margin-right: 24px;
        }

        .year {
          width: 100px;
        }
      }
    }

    .carDetails {
      margin-top: 36px;

      h6 {
        margin-bottom: 16px;
      }

      .year {
        width: 100px;
      }

      .shortGroup {
        width: 144px;
      }
    }

  }
</style>
